import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store";
import { updatePeriod } from "../../reducers/period";
import { getCurrentPeriod } from "../util/period";
import { EVENTS } from "../enums/events";
import { innerPathnameLogic, periodSelectorRoutes } from "../util/routeUtils";

const EMPTY_ROUTES: string[] = [];

/**
 * Hook that determines period selector display:
 * - Calculates visibility based on route configuration
 * - Handles dynamic route parameters
 * - Listens to inner route events from imported components
 *
 * @returns displayPeriodSelector - Whether the period selector should be shown
 */
export const usePeriodSelectorVisibility = () => {
  const { pathname, hash } = useLocation();
  const urlParams = useParams();
  const aboParam = urlParams["abo"];
  const dispatch = useAppDispatch();
  const removePeriodSelectorFromRoute =
    useAppSelector((state) => state.boot.configuration.removePeriodSelectorFromRoute) ?? EMPTY_ROUTES;
  const [displayPeriodSelector, setPeriodSelectorDisplay] = useState(true);
  const lastInnerPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    lastInnerPathnameRef.current = null;
  }, [pathname]);

  useEffect(() => {
    const setPeriodSelector = () => {
      const shouldShow = periodSelectorRoutes(pathname, removePeriodSelectorFromRoute, urlParams, hash);
      // HAT/GAR has a custom period selector - capping at the last processed period.
      // some of the internal routes don't have a period selector but reference the custom periods via `selectedPeriod`
      // ex: `/hat/leg-qc-details`, `/hat/leg-qc-details/:abo`, `/gar/leg-qc-details`, `/gar/leg-qc-details/:abo`
      const shouldUseCurrentPeriod = !(
        pathname === "/hat" ||
        pathname.startsWith("/hat/") ||
        pathname === "/gar" ||
        pathname.startsWith("/gar/") ||
        pathname === "/faa" ||
        pathname.startsWith("/faa/")
      );

      if (!shouldShow && shouldUseCurrentPeriod) {
        dispatch(updatePeriod({ period: getCurrentPeriod() }));
      }
      return setPeriodSelectorDisplay(shouldShow);
    };

    const applyInnerPathnameLogic = (innerPathname: string) => {
      if (innerPathnameLogic(innerPathname, "edlos")) {
        return setPeriodSelectorDisplay(!removePeriodSelectorFromRoute.includes(innerPathname));
      }
      if (innerPathnameLogic(innerPathname, "hat")) {
        return setPeriodSelectorDisplay(removePeriodSelectorFromRoute.includes(innerPathname));
      }
      setPeriodSelector();
    };

    // Re-apply inner path state when deps change (e.g. config re-render)
    // so the route-based reset doesn't override inner navigation state.
    if (lastInnerPathnameRef.current !== null) {
      applyInnerPathnameLogic(lastInnerPathnameRef.current);
    } else {
      setPeriodSelector();
    }

    const setPeriodSelectorOnInnerPath = () => {
      const eventBasedPathCheck = (event: CustomEventInit) => {
        const innerPathname = event?.detail?.pathname;

        if (innerPathnameLogic(innerPathname, "edlos")) {
          lastInnerPathnameRef.current = innerPathname;
          return setPeriodSelectorDisplay(!removePeriodSelectorFromRoute.includes(innerPathname));
        }

        // Remove the period selector when on the inner route for the HAT QC Details page.
        if (innerPathnameLogic(innerPathname, "hat")) {
          lastInnerPathnameRef.current = innerPathname;
          return setPeriodSelectorDisplay(removePeriodSelectorFromRoute.includes(innerPathname));
        }

        lastInnerPathnameRef.current = null;
        return setPeriodSelector();
      };

      document.addEventListener(EVENTS.EVENT_LISTENER.ACC_ES_ROUTER_GO, eventBasedPathCheck);
      return () => {
        document.removeEventListener(EVENTS.EVENT_LISTENER.ACC_ES_ROUTER_GO, eventBasedPathCheck);
      };
    };

    const cleanup = setPeriodSelectorOnInnerPath();
    return cleanup;
  }, [pathname, removePeriodSelectorFromRoute, aboParam, hash]);

  return displayPeriodSelector;
};
